import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import { usePaymentSummary } from '@/api/payments/queries/usePaymentSummary'
import { usePkgPrices } from '@/api/payments/queries/usePkgPrices'
import { PkgPricesResponse } from '@/api/payments/types/PkgPricesResponse'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { PkgShippingMethod } from '@/api/user-package/types/UserPkg'
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs'
import { PaymentSuccessDialog } from '@/components/PaymentSuccessDialog'
import { Spinner } from '@/components/ui/spinner'
import { useToast } from '@/hooks/useToast'
import { trackPaymentSuccessful } from '@/lib/analytics'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { Stepper } from '../ReceiptUpload/components/Stepper'
import { PaymentData } from './components/PaymentData'
import ProcessPage from './ProcessPage'
import ShippingPage, { ShippingOptions } from './ShippingPage'
import SummaryPage from './SummaryPage'

export default function PaymentPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: userPkgs, isLoading } = useUserPkgs()
  const location = useLocation()
  const { pkgId: pkgIdParam, step } = useParams<{ pkgId: string; step: string }>()
  const pkgId = pkgIdParam ?? ''
  const pkg = userPkgs?.find(p => p.id === pkgId)
  const { data: pkgPrices } = usePkgPrices(pkgId)
  const { data: addresses } = useAddresses()
  const { showCustomToast } = useToast()
  const [shippingOptions, setShippingOptions] = useState<ShippingOptions>({
    selectedAddressId: '',
    selectedShippingMethod: PkgShippingMethod.DoorToDoor,
    isInsuranceAdded: false,
  })
  const [paymentData, setPaymentData] = useState<PaymentData>({
    couponCode: '',
    coupon: null,
    feesDiscount: 0,
    israeliId: '',
    paymentLinkData: null,
    creditToUseUSD: 0,
  })
  const { data: summaryData, isFetching: isLoadingSummary } = usePaymentSummary({
    pkgId: pkg?.id ?? '',
    isInsuranceAdded: shippingOptions.isInsuranceAdded,
    couponCode: paymentData.couponCode,
    creditToUseUSD: paymentData.creditToUseUSD,
    shippingMethod: shippingOptions.selectedShippingMethod,
  })
  const pkgValue = pkgPrices?.pkgValueInUSD ?? 0
  const shippingPrices: PkgPricesResponse['shippingPrices'] = pkgPrices?.shippingPrices ?? {
    [PkgShippingMethod.DoorToDoor]: 0,
    [PkgShippingMethod.PickUpPoint]: 0,
    [PkgShippingMethod.Test]: 0.1,
  }

  useEffect(() => {
    if (addresses && addresses.length > 0 && !shippingOptions.selectedAddressId) {
      setShippingOptions(prev => ({ ...prev, selectedAddressId: addresses.find(addr => addr.isDefault)?.id || addresses[0].id }))
    }
  }, [addresses, shippingOptions.selectedAddressId])

  useEffect(() => {
    if (!isLoading && !pkg) {
      navigate(RoutePath.ClientPkg)
      return
    }

    if (pkg && (!pkg.buyingReciptKey || !pkg.price)) {
      navigate(`${RoutePath.ReceiptUpload}/${pkgId}`)
      return
    }
    if (pkg && pkg.shippingMethod) {
      setShippingOptions(prev => ({ ...prev, selectedShippingMethod: pkg.shippingMethod }))
    }
    if (pkg && pkg.isPaid) {
      navigate(RoutePath.ClientPkg)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg, isLoading, pkgId])
  const queryClient = useQueryClient()

  const completePaymentSuccess = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    const displayPrice = parseFloat((summaryData?.finalPriceILS ?? 0).toFixed(2))
    trackPaymentSuccessful({
      packageId: pkgId,
      value: displayPrice,
    })
    showCustomToast(close => (
      <PaymentSuccessDialog shippingMethod={shippingOptions.selectedShippingMethod} displayPrice={displayPrice} onClose={close} />
    ))
  }, [queryClient, summaryData?.finalPriceILS, pkgId, showCustomToast, shippingOptions.selectedShippingMethod])

  const updateShippingOptions = (options: Partial<ShippingOptions>) => {
    setShippingOptions(prev => ({ ...prev, ...options }))
  }

  const updatePaymentData = (data: Partial<PaymentData>) => {
    setPaymentData(prev => ({ ...prev, ...data }))
  }

  const currentStep = useMemo(() => {
    if (location.pathname.includes(RoutePath.PaymentProcess)) {
      return 3
    }
    if (location.pathname.includes(RoutePath.PaymentSummary)) {
      return 2
    }
    if (location.pathname.includes(RoutePath.PaymentShipping)) {
      return 1
    }
    return 0
  }, [location.pathname])

  const breadcrumbSteps = useMemo(() => {
    const res = [
      { label: t('clientTabs.packages'), to: RoutePath.ClientPkg },
      { label: t('payment.packageDeclaration'), to: `${RoutePath.ReceiptUpload}/${pkgId}` },
    ]
    if (currentStep >= 1) {
      res.push({ label: t('payment.selectShipping'), to: `${RoutePath.PaymentShipping}/${pkgId}` })
    }
    if (currentStep >= 2) {
      res.push({ label: t('payment.summaryAndConfirm'), to: `${RoutePath.PaymentSummary}/${pkgId}` })
    }
    if (currentStep >= 3) {
      res.push({ label: t('payment.payment'), to: `${RoutePath.PaymentProcess}/${pkgId}` })
    }
    return res
  }, [currentStep, pkgId, t])

  return (
    <div className='mx-auto min-h-full max-w-5xl p-6' dir='rtl'>
      {/* Header */}
      <div className='mb-6'>
        <AppBreadcrumbs steps={breadcrumbSteps} />
      </div>
      <div className='mx-auto max-w-xl'>
        <div className='mb-6 flex justify-center'>
          <Stepper currentStep={currentStep} totalSteps={4} />
        </div>

        {/* Child Components */}
        {isLoading || !pkg ? (
          <Spinner className='m-auto size-10' />
        ) : step === 'shipping' ? (
          <ShippingPage
            pkg={pkg}
            shippingOptions={shippingOptions}
            onUpdateShippingOptions={updateShippingOptions}
            shippingPrices={shippingPrices}
            insurancePrice={pkgPrices?.insurancePrice ?? 0}
          />
        ) : step === 'summary' ? (
          <SummaryPage
            pkg={pkg}
            shippingOptions={shippingOptions}
            summaryData={summaryData}
            isLoadingSummary={isLoadingSummary}
            pkgValue={pkgValue}
            paymentData={paymentData}
            onUpdatePaymentData={updatePaymentData}
            onCompletePaymentSuccess={completePaymentSuccess}
          />
        ) : step === 'process' ? (
          <ProcessPage
            key={pkgId}
            paymentLinkData={paymentData.paymentLinkData}
            pkgId={pkgId}
            onCompletePaymentSuccess={completePaymentSuccess}
          />
        ) : null}
      </div>
    </div>
  )
}
